// Match results for played Mundial 2026 fixtures: final score, goalscorers
// (player + minute), and any penalty shootout. The calendar renders these for
// games that have already been played and leaves the rest as upcoming.
//
// ── IMPORTANT: never fabricate ─────────────────────────────────────────────
// Only enter OFFICIALLY CONFIRMED results. A match stays "upcoming" until its
// id appears in RESULTS below — so leave a fixture out rather than guessing a
// score, scorer or minute. This mirrors the no-invented-data rule in
// fixtures.ts (rosters) and broadcast.ts (channels).

import type { Match } from "./fixtures";

export type GoalSide = "home" | "away";
/** Plain goal, penalty ("pen"), or own goal ("og"). */
export type GoalKind = "goal" | "pen" | "og";

export type Goal = {
  /** Which side the goal counts FOR (own goals count for the opponent). */
  side: GoalSide;
  /** Scorer's name as officially listed. */
  player: string;
  /** Match minute (1–120). For stoppage time use `minute` + `minutePlus`. */
  minute: number;
  /** Stoppage-time addition, e.g. 90+3 → { minute: 90, minutePlus: 3 }. */
  minutePlus?: number;
  /** Defaults to "goal". */
  kind?: GoalKind;
};

export type MatchResult = {
  homeGoals: number;
  awayGoals: number;
  /** Goals in chronological order (used for the scorer timeline). */
  goals: Goal[];
  /** Knockout shootout score, when a draw was decided on penalties. */
  pens?: { home: number; away: number };
};

// ── RESULTS: fill in as matches finish ─────────────────────────────────────
// Keyed by Match.id ("M001"..). Example shape (DO NOT ship invented data):
//
//   "M001": {
//     homeGoals: 2,
//     awayGoals: 1,
//     goals: [
//       { side: "home", player: "Scorer A", minute: 12 },
//       { side: "away", player: "Scorer B", minute: 58 },
//       { side: "home", player: "Scorer C", minute: 90, minutePlus: 2, kind: "pen" },
//     ],
//   },
//
// Knockout decided on penalties:
//   "M073": { homeGoals: 1, awayGoals: 1, goals: [...], pens: { home: 4, away: 2 } },
//
// Coverage: all 72 group matches (M001–M072), the full Round of 32 (M073–M088),
// the Round of 16 games played so far (M089–M094), all four quarter-finals
// (M097–M100) and both semi-finals (M101–M102). The last two R16 games (M095,
// M096) are still awaiting a confirmed score; the final (M104, Spain–Argentina)
// and third-place (M103, France–England) are set but not yet played. Source: Wikipedia
// "2026 FIFA World Cup" group + knockout articles (scores cross-checked between
// each match's football-box and the tournament bracket). Final scores only —
// `goals` is left empty because per-goal minutes are populated only from
// officially confirmed scorer data; the scorer leaderboard lives in TOP_SCORERS.
export const RESULTS: Record<string, MatchResult> = {
  // Group stage — Thu 11 Jun
  M001: { homeGoals: 2, awayGoals: 0, goals: [] }, // Mexico 2–0 South Africa
  // Group stage — Fri 12 Jun
  M002: { homeGoals: 2, awayGoals: 1, goals: [] }, // South Korea 2–1 Czechia
  M003: { homeGoals: 1, awayGoals: 1, goals: [] }, // Canada 1–1 Bosnia & Herzegovina
  // Group stage — Sat 13 Jun
  M004: { homeGoals: 4, awayGoals: 1, goals: [] }, // USA 4–1 Paraguay
  M005: { homeGoals: 1, awayGoals: 1, goals: [] }, // Qatar 1–1 Switzerland
  M006: { homeGoals: 1, awayGoals: 1, goals: [] }, // Brazil 1–1 Morocco
  // Group stage — Sun 14 Jun
  M007: { homeGoals: 0, awayGoals: 1, goals: [] }, // Haiti 0–1 Scotland
  M008: { homeGoals: 2, awayGoals: 0, goals: [] }, // Australia 2–0 Türkiye
  M009: { homeGoals: 7, awayGoals: 1, goals: [] }, // Germany 7–1 Curaçao
  M010: { homeGoals: 2, awayGoals: 2, goals: [] }, // Netherlands 2–2 Japan
  // Group stage — Mon 15 Jun
  M011: { homeGoals: 1, awayGoals: 0, goals: [] }, // Ivory Coast 1–0 Ecuador
  M012: { homeGoals: 5, awayGoals: 1, goals: [] }, // Sweden 5–1 Tunisia
  M013: { homeGoals: 0, awayGoals: 0, goals: [] }, // Spain 0–0 Cape Verde
  M014: { homeGoals: 1, awayGoals: 1, goals: [] }, // Belgium 1–1 Egypt
  M015: { homeGoals: 1, awayGoals: 1, goals: [] }, // Saudi Arabia 1–1 Uruguay
  M016: { homeGoals: 2, awayGoals: 2, goals: [] }, // Iran 2–2 New Zealand
  // Group stage — Tue 16 Jun
  M017: { homeGoals: 3, awayGoals: 1, goals: [] }, // France 3–1 Senegal
  M018: { homeGoals: 1, awayGoals: 4, goals: [] }, // Iraq 1–4 Norway
  M019: { homeGoals: 3, awayGoals: 0, goals: [] }, // Argentina 3–0 Algeria
  M020: { homeGoals: 3, awayGoals: 1, goals: [] }, // Austria 3–1 Jordan
  // Group stage — Wed 17 Jun
  M021: { homeGoals: 1, awayGoals: 1, goals: [] }, // Portugal 1–1 DR Congo
  M022: { homeGoals: 4, awayGoals: 2, goals: [] }, // England 4–2 Croatia
  M023: { homeGoals: 1, awayGoals: 0, goals: [] }, // Ghana 1–0 Panama
  M024: { homeGoals: 1, awayGoals: 3, goals: [] }, // Uzbekistan 1–3 Colombia
  // Group stage — Thu 18 Jun
  M025: { homeGoals: 1, awayGoals: 1, goals: [] }, // Czechia 1–1 South Africa
  M026: { homeGoals: 4, awayGoals: 1, goals: [] }, // Switzerland 4–1 Bosnia & Herzegovina
  M027: { homeGoals: 6, awayGoals: 0, goals: [] }, // Canada 6–0 Qatar
  M028: { homeGoals: 1, awayGoals: 0, goals: [] }, // Mexico 1–0 South Korea
  // Group stage — Fri 19 Jun
  M029: { homeGoals: 2, awayGoals: 0, goals: [] }, // USA 2–0 Australia
  M030: { homeGoals: 0, awayGoals: 1, goals: [] }, // Scotland 0–1 Morocco
  M031: { homeGoals: 3, awayGoals: 0, goals: [] }, // Brazil 3–0 Haiti
  M032: { homeGoals: 0, awayGoals: 1, goals: [] }, // Türkiye 0–1 Paraguay
  // Group stage — Sat 20 Jun
  M033: { homeGoals: 5, awayGoals: 1, goals: [] }, // Netherlands 5–1 Sweden
  M034: { homeGoals: 2, awayGoals: 1, goals: [] }, // Germany 2–1 Ivory Coast
  M035: { homeGoals: 0, awayGoals: 0, goals: [] }, // Ecuador 0–0 Curaçao
  M036: { homeGoals: 0, awayGoals: 4, goals: [] }, // Tunisia 0–4 Japan
  // Group stage — Sun 21 Jun
  M037: { homeGoals: 4, awayGoals: 0, goals: [] }, // Spain 4–0 Saudi Arabia
  M038: { homeGoals: 0, awayGoals: 0, goals: [] }, // Belgium 0–0 Iran
  M039: { homeGoals: 2, awayGoals: 2, goals: [] }, // Uruguay 2–2 Cape Verde
  M040: { homeGoals: 1, awayGoals: 3, goals: [] }, // New Zealand 1–3 Egypt
  // Group stage — Mon 22 Jun
  M041: { homeGoals: 2, awayGoals: 0, goals: [] }, // Argentina 2–0 Austria
  M042: { homeGoals: 3, awayGoals: 0, goals: [] }, // France 3–0 Iraq
  M043: { homeGoals: 3, awayGoals: 2, goals: [] }, // Norway 3–2 Senegal
  M044: { homeGoals: 1, awayGoals: 2, goals: [] }, // Jordan 1–2 Algeria
  // Group stage — Tue 23 Jun
  M045: { homeGoals: 5, awayGoals: 0, goals: [] }, // Portugal 5–0 Uzbekistan
  M046: { homeGoals: 0, awayGoals: 0, goals: [] }, // England 0–0 Ghana
  M047: { homeGoals: 0, awayGoals: 1, goals: [] }, // Panama 0–1 Croatia
  M048: { homeGoals: 1, awayGoals: 0, goals: [] }, // Colombia 1–0 DR Congo
  // Group stage — final round (24–27 Jun)
  M049: { homeGoals: 2, awayGoals: 1, goals: [] }, // Switzerland 2–1 Canada
  M050: { homeGoals: 3, awayGoals: 1, goals: [] }, // Bosnia & Herzegovina 3–1 Qatar
  M051: { homeGoals: 0, awayGoals: 3, goals: [] }, // Scotland 0–3 Brazil
  M052: { homeGoals: 4, awayGoals: 2, goals: [] }, // Morocco 4–2 Haiti
  M053: { homeGoals: 0, awayGoals: 3, goals: [] }, // Czechia 0–3 Mexico
  M054: { homeGoals: 1, awayGoals: 0, goals: [] }, // South Africa 1–0 South Korea
  M055: { homeGoals: 2, awayGoals: 1, goals: [] }, // Ecuador 2–1 Germany
  M056: { homeGoals: 0, awayGoals: 2, goals: [] }, // Curaçao 0–2 Ivory Coast
  M057: { homeGoals: 1, awayGoals: 1, goals: [] }, // Japan 1–1 Sweden
  M058: { homeGoals: 1, awayGoals: 3, goals: [] }, // Tunisia 1–3 Netherlands
  M059: { homeGoals: 3, awayGoals: 2, goals: [] }, // Türkiye 3–2 USA
  M060: { homeGoals: 0, awayGoals: 0, goals: [] }, // Paraguay 0–0 Australia
  M061: { homeGoals: 1, awayGoals: 4, goals: [] }, // Norway 1–4 France
  M062: { homeGoals: 5, awayGoals: 0, goals: [] }, // Senegal 5–0 Iraq
  M063: { homeGoals: 0, awayGoals: 0, goals: [] }, // Cape Verde 0–0 Saudi Arabia
  M064: { homeGoals: 0, awayGoals: 1, goals: [] }, // Uruguay 0–1 Spain
  M065: { homeGoals: 1, awayGoals: 1, goals: [] }, // Egypt 1–1 Iran
  M066: { homeGoals: 1, awayGoals: 5, goals: [] }, // New Zealand 1–5 Belgium
  M067: { homeGoals: 0, awayGoals: 2, goals: [] }, // Panama 0–2 England
  M068: { homeGoals: 2, awayGoals: 1, goals: [] }, // Croatia 2–1 Ghana
  M069: { homeGoals: 0, awayGoals: 0, goals: [] }, // Colombia 0–0 Portugal
  M070: { homeGoals: 3, awayGoals: 1, goals: [] }, // DR Congo 3–1 Uzbekistan
  M071: { homeGoals: 3, awayGoals: 3, goals: [] }, // Algeria 3–3 Austria
  M072: { homeGoals: 1, awayGoals: 3, goals: [] }, // Jordan 1–3 Argentina
  // Round of 32 (28 Jun – 3 Jul)
  M073: { homeGoals: 0, awayGoals: 1, goals: [] }, // South Africa 0–1 Canada
  M074: { homeGoals: 2, awayGoals: 1, goals: [] }, // Brazil 2–1 Japan
  M075: { homeGoals: 1, awayGoals: 1, goals: [], pens: { home: 3, away: 4 } }, // Germany 1–1 Paraguay (3–4 pens)
  M076: { homeGoals: 1, awayGoals: 1, goals: [], pens: { home: 2, away: 3 } }, // Netherlands 1–1 Morocco (2–3 pens)
  M077: { homeGoals: 1, awayGoals: 2, goals: [] }, // Ivory Coast 1–2 Norway
  M078: { homeGoals: 3, awayGoals: 0, goals: [] }, // France 3–0 Sweden
  M079: { homeGoals: 2, awayGoals: 0, goals: [] }, // Mexico 2–0 Ecuador
  M080: { homeGoals: 2, awayGoals: 1, goals: [] }, // England 2–1 DR Congo
  M081: { homeGoals: 3, awayGoals: 2, goals: [] }, // Belgium 3–2 Senegal (a.e.t.)
  M082: { homeGoals: 2, awayGoals: 0, goals: [] }, // USA 2–0 Bosnia & Herzegovina
  M083: { homeGoals: 3, awayGoals: 0, goals: [] }, // Spain 3–0 Austria
  M084: { homeGoals: 2, awayGoals: 1, goals: [] }, // Portugal 2–1 Croatia
  M085: { homeGoals: 2, awayGoals: 0, goals: [] }, // Switzerland 2–0 Algeria
  M086: { homeGoals: 1, awayGoals: 1, goals: [], pens: { home: 2, away: 4 } }, // Australia 1–1 Egypt (2–4 pens)
  M087: { homeGoals: 3, awayGoals: 2, goals: [] }, // Argentina 3–2 Cape Verde (a.e.t.)
  M088: { homeGoals: 1, awayGoals: 0, goals: [] }, // Colombia 1–0 Ghana
  // Round of 16 (played so far)
  M089: { homeGoals: 0, awayGoals: 3, goals: [] }, // Canada 0–3 Morocco
  M090: { homeGoals: 0, awayGoals: 1, goals: [] }, // Paraguay 0–1 France
  M091: { homeGoals: 1, awayGoals: 2, goals: [] }, // Brazil 1–2 Norway
  M092: { homeGoals: 2, awayGoals: 3, goals: [] }, // Mexico 2–3 England
  M093: { homeGoals: 0, awayGoals: 1, goals: [] }, // Portugal 0–1 Spain
  M094: { homeGoals: 1, awayGoals: 4, goals: [] }, // USA 1–4 Belgium
  // Quarter-finals (9–12 Jul). NOTE: M095 (Argentina–Egypt) and M096
  // (Switzerland–Colombia), the last two Round-of-16 games, are still awaiting a
  // confirmed score, so they intentionally stay "upcoming" even though their
  // winners (Argentina, Switzerland) are already known from the QF bracket.
  M097: { homeGoals: 2, awayGoals: 0, goals: [] }, // France 2–0 Morocco
  M098: { homeGoals: 2, awayGoals: 1, goals: [] }, // Spain 2–1 Belgium
  M099: { homeGoals: 1, awayGoals: 2, goals: [] }, // Norway 1–2 England (a.e.t.)
  M100: { homeGoals: 3, awayGoals: 1, goals: [] }, // Argentina 3–1 Switzerland (a.e.t.)
  // Semi-finals (14–15 Jul)
  M101: { homeGoals: 0, awayGoals: 2, goals: [] }, // France 0–2 Spain
  M102: { homeGoals: 1, awayGoals: 2, goals: [] }, // England 1–2 Argentina
};

export function resultFor(match: Pick<Match, "id">): MatchResult | undefined {
  return RESULTS[match.id];
}

export type Outcome = "home" | "away" | "draw";

/** Winner of a played match (penalties break a knockout draw). */
export function outcomeOf(r: MatchResult): Outcome {
  if (r.homeGoals !== r.awayGoals) {
    return r.homeGoals > r.awayGoals ? "home" : "away";
  }
  if (r.pens) return r.pens.home > r.pens.away ? "home" : "away";
  return "draw";
}

/** "23'" or "90+3'" — the displayed minute for a goal. */
export function goalMinuteLabel(g: Goal): string {
  return g.minutePlus ? `${g.minute}+${g.minutePlus}'` : `${g.minute}'`;
}

// ── TOP SCORERS ────────────────────────────────────────────────────────────
// The tournament goal-scoring leaderboard. Kept as its own confirmed dataset
// (rather than derived from RESULTS.goals) because RESULTS.goals is intentionally
// empty. Order: most goals first. Counts aggregated from the per-match scorers on
// Wikipedia and cross-checked against the adidas Golden Boot standings published
// on 6 Jul 2026 (Al Jazeera / Goal.com) — the top six match exactly. Assists are
// only shown where confirmed by that source. VERIFY before launch.
export type TopScorer = {
  player: string;
  /** Scorer's team flag/ISO code (must exist in TEAM_BY_CODE). */
  team: string;
  goals: number;
  assists?: number;
};

export const TOP_SCORERS: readonly TopScorer[] = [
  { player: "Kylian Mbappé", team: "fr", goals: 7, assists: 2 },
  { player: "Lionel Messi", team: "ar", goals: 7 },
  { player: "Erling Haaland", team: "no", goals: 7 },
  { player: "Harry Kane", team: "gb-eng", goals: 6, assists: 1 },
  { player: "Jude Bellingham", team: "gb-eng", goals: 4 },
  { player: "Ousmane Dembélé", team: "fr", goals: 4, assists: 2 },
  { player: "Ismaïla Sarr", team: "sn", goals: 4 },
  { player: "Mikel Oyarzabal", team: "es", goals: 4, assists: 1 },
  { player: "Julián Quiñones", team: "mx", goals: 4 },
  { player: "Vinícius Júnior", team: "br", goals: 4 },
];
